import { z } from 'zod';
import { BooleanQuery, Id, PaginationQuery, paginated, SortOrder } from './common';

/** Contrats des mots-clés (`api.md` § Mots-clés et clusters, fiche T14). */

/** `keywords.features` (T07), exposé tel quel (`snake_case`, forme du jsonb). */
export const KeywordFeatures = z.object({
  word_count: z.number().int(),
  has_task_verb: z.boolean(),
  task_verbs: z.array(z.string()),
  modifiers: z.array(z.string()),
  is_question: z.boolean(),
  is_informational: z.boolean(),
  is_news: z.boolean(),
  is_local: z.boolean(),
  has_file_format: z.boolean(),
  has_price_marker: z.boolean(),
  brand_guess: z.boolean(),
  /** Signal de marque fort seul (T35) ; absent des mots-clés écrits avant : vaut `false`. */
  brand_strong: z.boolean().optional(),
});
export type KeywordFeatures = z.infer<typeof KeywordFeatures>;

export const MonthlySearch = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  searches: z.number(),
});
export type MonthlySearch = z.infer<typeof MonthlySearch>;

export const KeywordSort = z.enum(['avgMonthlySearches', 'highTopOfPageBid', 'text']);
export type KeywordSort = z.infer<typeof KeywordSort>;

/** `GET /keywords` : `marketId` requis, `isPrimary` vaut `true` par défaut. */
export const KeywordsQuery = PaginationQuery.extend({
  marketId: Id,
  clusterId: Id.optional(),
  /** Recherche `ILIKE` sur `normalized_text`. */
  q: z.string().trim().min(1).max(200).optional(),
  isPrimary: BooleanQuery.default(true),
  sort: KeywordSort.default('avgMonthlySearches'),
  order: SortOrder.default('desc'),
});
export type KeywordsQuery = z.infer<typeof KeywordsQuery>;

/** Montants convertis en USD côté serveur (`markets.currency` × `FX_TO_USD`). */
export const KeywordRow = z.object({
  id: z.number().int(),
  text: z.string(),
  avgMonthlySearches: z.number().nullable(),
  monthlySearches: z.array(MonthlySearch),
  competitionIndex: z.number().nullable(),
  lowTopOfPageBidUsd: z.number().nullable(),
  highTopOfPageBidUsd: z.number().nullable(),
  averageCpcUsd: z.number().nullable(),
  features: KeywordFeatures,
  isPrimary: z.boolean(),
  /** Variantes lexicales repliées sur ce primaire (0 pour une variante). */
  variantCount: z.number().int(),
  dataMonth: z.string(),
});
export type KeywordRow = z.infer<typeof KeywordRow>;

export const KeywordsResponse = paginated(KeywordRow);
export type KeywordsResponse = z.infer<typeof KeywordsResponse>;
